import { useEffect, useRef } from 'react';
import { EvoIframeApi } from '@access-evo/evo-api';
import { initialiseMfes } from '@access-evo/mfe-host';
import type { ReactNode } from 'react';

interface MfeHostProviderProps {
  getAccessToken: () => Promise<string>;
  children: ReactNode;
}

/**
 * Bootstraps the Evo MFE host inside the iframe so that
 * window.AccessEvo.mfes is available for in-app MFE mounting
 * (e.g. the ADM Document Management panel).
 *
 * Environment, geography and user identity are bridged from the
 * parent workspace shell via EvoIframeApi; the access token comes
 * from the local OIDC session.
 */
export function MfeHostProvider({ getAccessToken, children }: MfeHostProviderProps) {
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    let cancelled = false;
    const iframeApi = new EvoIframeApi();

    (async () => {
      try {
        const [environment, geography] = await Promise.all([
          iframeApi.getEnvironment(),
          iframeApi.getGeography(),
        ]);

        if (cancelled) return;

        const getEvoUser = async () => {
          const user = await iframeApi.getEvoUser();
          return {
            id: user.id,
            emailAddress: user.emailAddress,
            organisation: { id: user.organisation.id },
          };
        };

        cleanupRef.current = initialiseMfes({
          hostName: 'access-projects',
          environment,
          geography,
          getEvoUser,
          getAccessToken: async () => getAccessToken(),
        });
      } catch (err) {
        if (!cancelled) {
          console.warn('[MfeHostProvider] Failed to initialise MFE host:', err);
        }
      }
    })();

    return () => {
      cancelled = true;
      iframeApi.destroy();
      cleanupRef.current?.();
      cleanupRef.current = null;
    };
  }, [getAccessToken]);

  return <>{children}</>;
}
